import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useSession } from '../lib/auth';
import { formatPercentFromBps } from '../lib/format';

/**
 * Menu do usuario no cabecalho (RNF12).
 *
 * Mostra quem esta logado e de qual empresa, e tem a unica acao que importa:
 * sair. A empresa aparece aqui e na lateral porque o painel e multi-tenant --
 * quem opera mais de uma loja precisa saber, sem clicar, qual esta aberta.
 */
export function UserMenu(): JSX.Element {
  const session = useSession();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [saindo, setSaindo] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    const onClick = (event: MouseEvent): void => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const iniciais = session.user.name
    .split(/\s+/)
    .filter((p) => p.length > 0)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');

  const sair = async (): Promise<void> => {
    setSaindo(true);
    try {
      await logout();
    } finally {
      setSaindo(false);
      navigate('/login', { replace: true });
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`Conta de ${session.user.name}`}
        className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-brand to-series-7 text-xs font-semibold text-white shadow-soft ring-2 ring-surface transition-transform hover:scale-105"
      >
        {iniciais === '' ? '?' : iniciais}
      </button>

      {open ? (
        <div
          id={menuId}
          role="menu"
          aria-label="Conta"
          className="absolute right-0 z-drawer mt-2 w-72 overflow-hidden rounded-xl border border-line bg-elevated shadow-float animate-fade-in"
        >
          <div className="border-b border-line px-4 py-3">
            <p className="truncate text-sm font-semibold text-ink">{session.user.name}</p>
            <p className="truncate text-xs text-ink-subtle">{session.user.email}</p>
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 px-4 py-3 text-xs">
            <dt className="text-ink-subtle">Empresa</dt>
            <dd className="truncate font-medium text-ink">{session.company.name}</dd>
            <dt className="text-ink-subtle">Comissão</dt>
            <dd className="tabular-nums text-ink">{formatPercentFromBps(session.company.feeBps)} sobre o recuperado</dd>
          </dl>
          <div className="border-t border-line p-1">
            <button
              type="button"
              role="menuitem"
              disabled={saindo}
              onClick={() => void sair()}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-ink transition-colors hover:bg-ink/5 disabled:opacity-60"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 text-ink-muted" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <path d="M9 4.5H6.5a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2H9M15.5 8l4 4-4 4M19.5 12H9.5" />
              </svg>
              {saindo ? 'Saindo…' : 'Sair da conta'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
